<template>
  <SogDialog
    ref="_dialog"
    :target="target"
    width="500px"
    :dialog-buttons="SogDialogButtons.OkCancel"
    @click-ok="onOk"
    @click-cancel="onCancel"
  >
    <SogTextBox
      v-model="_policyNumber"
      width="100%"
      placeholder="Policenummer"
    />
    <TempFileFormComponent
      ref="_uploader"
      v-model="_tempFileInfo"
    />
  </SogDialog>
</template>

<script setup lang="ts">
import {ref} from "vue";
import SogDialog from "@/shared/ui/SogDialog.vue";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import {SogDialogButtons} from "@/shared/ui/types/SogDialogButtons";
import TempFileFormComponent from "@/modules/FileUtils/components/TempFileFormComponent.vue";
import apiVehicleRegistration from "@/modules/VehicleRegistration/api";
import notificationService from "@/shared/services/NotificationService";

/**
 * Verknüpft eine Zulassung mit einer Police und speichert optional im selben Schritt einen Anhang.
 * Reihenfolge: erst verknüpfen, dann Anhang speichern – das Backend hängt den Anhang dann
 * automatisch auch an die Police.
 */
interface Props {
  vehicleRegistrationId?: string
  target?: string
}

const props = withDefaults(defineProps<Props>(), {
  vehicleRegistrationId: undefined,
  target: "#app"
})

const _emit = defineEmits<{
  (e: 'linked'): void
}>()

const _dialog = ref<InstanceType<typeof SogDialog> | null>(null)
const _uploader = ref<InstanceType<typeof TempFileFormComponent> | null>(null)
const _policyNumber = ref<string | undefined>("")
const _tempFileInfo = ref<{ id?: string, fileName?: string, name?: string } | undefined>(undefined)
const _busy = ref(false)

function show() {
  _dialog.value?.show()
}

function reset() {
  _policyNumber.value = ""
  _tempFileInfo.value = undefined
  _uploader.value?.clear()
}

function onCancel() {
  _dialog.value?.hide()
  reset()
}

async function onOk() {
  if (_busy.value) {
    return
  }
  if (!_policyNumber.value) {
    notificationService.error("Policenummer muss angegeben werden")
    return
  }
  if (!props.vehicleRegistrationId) {
    return
  }

  _busy.value = true
  try {
    const linked = await apiVehicleRegistration.insuranceLink(props.vehicleRegistrationId, _policyNumber.value, "", false)
    if (!linked) {
      return
    }

    let t_message = "Zulassung erfolgreich verknüpft"
    const t_file = _tempFileInfo.value
    if (t_file?.id) {
      const saved = await apiVehicleRegistration.addTempFile(t_file.id, props.vehicleRegistrationId, t_file.name, t_file.fileName)
      if (saved) {
        t_message += " und Anhang gespeichert"
      } else {
        notificationService.error("Zulassung wurde verknüpft, der Anhang konnte aber nicht gespeichert werden")
      }
    }
    notificationService.success(t_message)

    _dialog.value?.hide()
    reset()
    _emit("linked")
  } finally {
    _busy.value = false
  }
}

defineExpose({show})
</script>
